<template>
  <dashboard-page title="Mensageria">
    <template #actions>
      <b-dropdown
        no-caret
        right
        variant="secondary"
        id="mensageria-header-actions"
      >
        <template #button-content>
          <svg
            width="5"
            height="19"
            viewBox="0 0 5 19"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle cx="2.19231" cy="2.19231" r="2.19231" fill="#12576e" />
            <circle cx="2.19231" cy="9.49992" r="2.19231" fill="#12576e" />
            <circle cx="2.19231" cy="16.8078" r="2.19231" fill="#12576e" />
          </svg>
        </template>

        <b-dropdown-item @click="openSendPanel">
          Painel de envio
        </b-dropdown-item>
      </b-dropdown>
    </template>

    <base-header class="content__title content__title--calendar">
      <b-row align-v="center">
        <b-col class="m-0" cols="12" md="7">
          <span class="font-weight-normal text-primary text-center">
            <a
              href="#"
              :class="{ disabled: loading }"
              class="fullcalendar-btn-prev btn btn-sm btn-text py-1 vertical-middle mx-0"
              @click.prevent="prevPeriod"
            >
              <i
                class="no no-left-arrow font-size-lg text-primary vertical-middle"
              ></i>
            </a>

            <span
              class="booking-list-period-label font-weight-normal text-primary vertical-middle"
            >
              <span @click="openStartDatePicker">
                {{ periodStartDateLabel }}
              </span>
              <span> - </span>
              <span @click="openEndDatePicker">
                {{ periodEndDateLabel }}
              </span>
            </span>

            <date-picker
              ref="startDatePicker"
              v-model="startDate"
              type="date"
              format="dd/MM/yy"
              value-format="yyyy-MM-dd"
              :clearable="false"
              class="booking-list-hidden-datepicker"
              @change="onPeriodDateChange"
            />

            <date-picker
              ref="endDatePicker"
              v-model="endDate"
              type="date"
              format="dd/MM/yy"
              value-format="yyyy-MM-dd"
              :clearable="false"
              class="booking-list-hidden-datepicker"
              @change="onPeriodDateChange"
            />

            <a
              href="#"
              :class="{ disabled: loading }"
              class="fullcalendar-btn-next btn btn-sm btn-text py-1 vertical-middle mx-0"
              @click.prevent="nextPeriod"
            >
              <i
                class="no no-right-arrow font-size-lg text-primary vertical-middle"
              ></i>
            </a>
          </span>
        </b-col>

        <b-col cols="12" md="5" class="m-0 text-right pl-0 mt-2 mt-md-0">
          <div class="d-flex align-items-center justify-content-end w-100">
            <div class="bg-pick">
              <button
                type="button"
                class="btn btn-letter"
                :class="{ active: contextType === 'global' }"
                @click="setContextType('global')"
              >
                Plataforma
              </button>
              <button
                type="button"
                class="btn btn-letter"
                :class="{ active: contextType === 'gestor' }"
                @click="setContextType('gestor')"
              >
                Gestor
              </button>
              <button
                type="button"
                class="btn btn-letter"
                :class="{ active: contextType === 'produto' }"
                @click="setContextType('produto')"
              >
                Produto
              </button>
            </div>
          </div>
        </b-col>
      </b-row>

      <b-row align-v="center" class="mt-2" v-if="contextType !== 'global'">
        <b-col cols="12" md="5" lg="4" class="mb-2 mb-md-0">
          <b-form-select
            v-if="contextType === 'gestor'"
            v-model="placeUuid"
            :options="gestorSelectOptions"
            :disabled="filtersLoading || !gestorOptions.length"
            class="nps-product-select"
          />
          <b-form-select
            v-else
            v-model="productUuid"
            :options="productSelectOptions"
            :disabled="filtersLoading || !productOptions.length"
            class="nps-product-select"
          />
        </b-col>
        <b-col cols="12" md="7">
          <p class="text-muted small mb-0">{{ contextHint }}</p>
          <p v-if="filtersError" class="text-danger small mb-0">
            {{ filtersError }}
          </p>
        </b-col>
      </b-row>
    </base-header>

    <b-container fluid class="mt-3 mensageria-admin-body">
      <base-alert v-if="apiUnavailable" type="info" class="w-100 mb-3">
        Endpoints admin indisponíveis. Contrato:
        <code>GET /messenger/admin/filters|stats|models</code>
        (modelos = <code>notifications.status = 3</code>).
      </base-alert>

      <div v-if="loading && !stats" class="text-muted py-4">
        <i class="fas fa-spinner fa-spin mr-2"></i>carregando relatório...
      </div>

      <b-row>
        <b-col cols="12" lg="5" class="mb-4 mb-lg-0">
          <b-card class="nps-section-card h-100" no-body>
            <b-card-header
              class="nps-section-header d-flex flex-column flex-md-row justify-content-between align-items-md-center"
            >
              <span>Disparos no período</span>
              <span class="nps-section-hint">
                Volume e frequência (pesquisa de satisfação / Mensageria)
              </span>
            </b-card-header>
            <b-card-body>
              <MensageriaStatsPanel
                :loading="loading"
                :error="statsError"
                :stats="stats"
              />
              <hr class="my-3" />
              <MensageriaScheduledList
                :loading="scheduledLoading"
                :error="scheduledError"
                :items="scheduledItems"
                :total="scheduledTotal"
                :page="scheduledPage"
                :per-page="scheduledPerPage"
                :search="scheduledSearch"
                @search="onScheduledSearch"
                @page-change="onScheduledPageChange"
                @send-now="onScheduledSendNow"
              />
            </b-card-body>
          </b-card>
        </b-col>

        <b-col cols="12" lg="7">
          <b-card class="nps-section-card h-100" no-body>
            <b-card-header
              class="nps-section-header d-flex flex-column flex-md-row justify-content-between align-items-md-center"
            >
              <span>Modelos</span>
              <span class="nps-section-hint">
                <code>notifications</code> com status=3 · mapa MODELO_* do .env
              </span>
            </b-card-header>
            <b-card-body>
              <MensageriaModelsList
                :loading="modelsLoading"
                :error="modelsError"
                :models="models"
              >
                <template #env-warning>
                  <EnvMappingWarning
                    :unmapped-models="unmappedModels"
                    :removed-from-env-models="removedFromEnvModels"
                  />
                </template>
              </MensageriaModelsList>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </b-container>

    <MensageriaSendPanel
      :visible="showSendPanel"
      :models="models"
      :loading="modelsLoading"
      :error="modelsError"
      @change="onSendPanelChange"
      @sent="onSendPanelSent"
    />
  </dashboard-page>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import { DatePicker } from 'element-ui'
import nodeService from '@/services/nodejs'
import MensageriaStatsPanel from './MensageriaStatsPanel.vue'
import MensageriaScheduledList from './MensageriaScheduledList.vue'
import MensageriaModelsList from './MensageriaModelsList.vue'
import EnvMappingWarning from './EnvMappingWarning.vue'
import MensageriaSendPanel from './MensageriaSendPanel.vue'

moment.locale('pt-br')

export default {
  name: 'MensageriaAdminDashboard',
  components: {
    DatePicker,
    MensageriaStatsPanel,
    MensageriaScheduledList,
    MensageriaModelsList,
    EnvMappingWarning,
    MensageriaSendPanel,
  },
  data() {
    return {
      contextType: 'global',
      placeUuid: null,
      productUuid: null,
      /** inclusive max days in period (start..end) */
      rangeLimit: 31,
      startDate: moment().startOf('month').format('YYYY-MM-DD'),
      endDate: moment().endOf('month').format('YYYY-MM-DD'),
      filtersLoading: false,
      loading: false,
      modelsLoading: false,
      scheduledLoading: false,
      apiUnavailable: false,
      filtersError: '',
      statsError: '',
      modelsError: '',
      scheduledError: '',
      gestores: [],
      products: [],
      stats: null,
      /** fila status==2 (agendada); send_date em GMT-0 na API */
      scheduledItems: [],
      scheduledTotal: 0,
      scheduledPage: 1,
      scheduledPerPage: 10,
      /** → query q (to/title/message/model_name; cobre message_data) */
      scheduledSearch: '',
      /** notifications.status = 3 */
      models: [],
      unmappedModels: [],
      removedFromEnvModels: [],
      showSendPanel: false,
    }
  },
  computed: {
    ...mapGetters('auth', {
      user: 'user',
    }),
    periodFrom() {
      return this.startDate
    },
    periodTo() {
      return this.endDate
    },
    periodStartDateLabel() {
      const start = moment(this.startDate)
      if (!start.isValid()) return '--'
      return start.format('DD/MM/YY')
    },
    periodEndDateLabel() {
      const end = moment(this.endDate)
      if (!end.isValid()) return '--'
      return end.format('DD/MM/YY')
    },
    gestorOptions() {
      return Array.isArray(this.gestores) ? this.gestores : []
    },
    productOptions() {
      return Array.isArray(this.products) ? this.products : []
    },
    gestorSelectOptions() {
      return [
        { value: null, text: 'Selecionar gestor (janoo)', disabled: true },
        ...this.gestorOptions.map((g) => ({
          value: g.uuid,
          text: g.name || g.uuid,
        })),
      ]
    },
    productSelectOptions() {
      return [
        { value: null, text: 'Selecionar produto (Passeio)', disabled: true },
        ...this.productOptions.map((p) => ({
          value: p.uuid,
          text: this.productLabel(p),
        })),
      ]
    },
    canGenerate() {
      if (this.contextType === 'gestor') return !!this.placeUuid
      if (this.contextType === 'produto') return !!this.productUuid
      return true
    },
    contextHint() {
      if (this.contextType === 'gestor') {
        return 'Gestores usertype=janoo · filtro place_uuid'
      }
      return 'Produtos sale_unit=Passeio · filtro product_uuid'
    },
  },
  mounted() {
    Promise.all([this.loadFilters(), this.loadModels()]).then(() =>
      this.gerar()
    )
  },
  methods: {
    openSendPanel() {
      this.showSendPanel = true
    },
    onSendPanelChange(visible) {
      this.showSendPanel = !!visible
    },
    onSendPanelSent() {
      // Refresh stats + Agendadas after admin create (status=2)
      this.gerar()
    },
    setContextType(type) {
      this.contextType = type
      if (type === 'global') {
        this.placeUuid = null
        this.productUuid = null
      }
      if (type === 'gestor') this.productUuid = null
      if (type === 'produto') this.placeUuid = null
    },
    productLabel(item) {
      if (!item) return ''
      const place = item.placeName ? ` (${item.placeName})` : ''
      return `${item.name || item.uuid || item.id}${place}`
    },
    openStartDatePicker() {
      const picker = this.$refs.startDatePicker
      if (!picker) return
      if (typeof picker.focus === 'function') picker.focus()
      picker.pickerVisible = true
    },
    openEndDatePicker() {
      const picker = this.$refs.endDatePicker
      if (!picker) return
      if (typeof picker.focus === 'function') picker.focus()
      picker.pickerVisible = true
    },
    closePeriodPickers() {
      ;['startDatePicker', 'endDatePicker'].forEach((ref) => {
        const picker = this.$refs[ref]
        if (picker) picker.pickerVisible = false
      })
    },
    /**
     * Keep start_date < end_date (swap) and clamp inclusive span to rangeLimit days.
     */
    normalizePeriodDates() {
      if (!this.startDate) {
        this.startDate = moment().startOf('month').format('YYYY-MM-DD')
      }
      if (!this.endDate) {
        this.endDate = moment().endOf('month').format('YYYY-MM-DD')
      }

      let start = moment(this.startDate)
      let end = moment(this.endDate)
      if (!start.isValid()) {
        start = moment().startOf('month')
        this.startDate = start.format('YYYY-MM-DD')
      }
      if (!end.isValid()) {
        end = moment().endOf('month')
        this.endDate = end.format('YYYY-MM-DD')
      }

      if (start.isAfter(end)) {
        const tmp = this.startDate
        this.startDate = this.endDate
        this.endDate = tmp
        start = moment(this.startDate)
        end = moment(this.endDate)
      }

      const inclusiveDays = end.diff(start, 'days') + 1
      if (inclusiveDays > this.rangeLimit) {
        this.endDate = start
          .clone()
          .add(this.rangeLimit - 1, 'days')
          .format('YYYY-MM-DD')
        if (typeof this.$notify === 'function') {
          this.$notify({
            type: 'warning',
            message:
              'O intervalo máximo é de ' +
              this.rangeLimit +
              ' dias. Intervalo ajustado.',
          })
        }
      }
    },
    onPeriodDateChange() {
      this.normalizePeriodDates()
      this.closePeriodPickers()
      this.gerar()
    },
    periodSpanDays() {
      this.normalizePeriodDates()
      const days =
        moment(this.endDate).diff(moment(this.startDate), 'days') + 1
      return Math.max(1, Math.min(days, this.rangeLimit))
    },
    prevPeriod() {
      const span = this.periodSpanDays()
      this.startDate = moment(this.startDate)
        .subtract(span, 'days')
        .format('YYYY-MM-DD')
      this.endDate = moment(this.endDate)
        .subtract(span, 'days')
        .format('YYYY-MM-DD')
      this.normalizePeriodDates()
      this.gerar()
    },
    nextPeriod() {
      const span = this.periodSpanDays()
      this.startDate = moment(this.startDate)
        .add(span, 'days')
        .format('YYYY-MM-DD')
      this.endDate = moment(this.endDate)
        .add(span, 'days')
        .format('YYYY-MM-DD')
      this.normalizePeriodDates()
      this.gerar()
    },
    queryParams() {
      this.normalizePeriodDates()
      const params = {
        start_date: this.periodFrom,
        end_date: this.periodTo,
      }
      if (this.contextType === 'gestor' && this.placeUuid) {
        params.place_uuid = this.placeUuid
      }
      if (this.contextType === 'produto' && this.productUuid) {
        params.product_uuid = this.productUuid
      }
      return params
    },
    isNotFound(err) {
      return err && err.response && err.response.status === 404
    },
    normalizeFiltersPayload(data) {
      const raw = data || {}
      const gestores = Array.isArray(raw.gestores) ? raw.gestores : []
      const products = Array.isArray(raw.products) ? raw.products : []
      return {
        gestores: gestores
          .map((g) => ({
            uuid: g.uuid || g.place_uuid || g.placeUuid || null,
            name: g.name || g.place_name || g.placeName || '',
          }))
          .filter((g) => g.uuid),
        products: products
          .map((p) => ({
            id: p.id,
            uuid: p.uuid || null,
            name: p.name || '',
            placeUuid: p.placeUuid || p.place_uuid || null,
            placeName: p.placeName || p.place_name || null,
          }))
          .filter((p) => p.uuid),
      }
    },
    async loadFilters() {
      this.filtersLoading = true
      this.filtersError = ''
      try {
        const response = await nodeService.getMessengerAdminFilters()
        const normalized = this.normalizeFiltersPayload(
          response && response.data
        )
        this.gestores = normalized.gestores
        this.products = normalized.products
        this.apiUnavailable = false
        if (!this.gestores.length && !this.products.length) {
          this.filtersError =
            'Filtros vazios: nenhum gestor janoo ou produto Passeio.'
        }
      } catch (err) {
        this.gestores = []
        this.products = []
        if (this.isNotFound(err)) {
          this.apiUnavailable = true
          this.filtersError = 'GET /messenger/admin/filters indisponível.'
        } else {
          this.filtersError = 'Não foi possível carregar filtros.'
        }
      } finally {
        this.filtersLoading = false
      }
    },
    /**
     * Modelos = apenas notifications.status = 3 (nunca a fila / messages).
     * GET /messenger/admin/models
     */
    async loadModels() {
      this.modelsLoading = true
      this.modelsError = ''
      try {
        const response = await nodeService.getMessengerAdminModels()
        const data = (response && response.data) || {}
        this.models = Array.isArray(data.models) ? data.models : []
        this.unmappedModels = Array.isArray(data.unmappedModels)
          ? data.unmappedModels
          : this.models.filter((m) => m && m.mappedInEnv === false)
        // "Removidos do .env": status=3 no banco sem MODELO_* (campo dedicado se a API enviar)
        this.removedFromEnvModels = Array.isArray(data.removedFromEnvModels)
          ? data.removedFromEnvModels
          : []
        this.apiUnavailable = false
      } catch (err) {
        this.models = []
        this.unmappedModels = []
        this.removedFromEnvModels = []
        if (this.isNotFound(err)) {
          this.apiUnavailable = true
          this.modelsError =
            'GET /messenger/admin/models indisponível (notifications.status=3).'
        } else {
          this.modelsError = 'Não foi possível carregar os modelos.'
        }
      } finally {
        this.modelsLoading = false
      }
    },
    onScheduledSearch(value) {
      this.scheduledSearch = value == null ? '' : String(value)
      this.scheduledPage = 1
      this.loadScheduled(this.queryParams())
    },
    onScheduledPageChange(page) {
      this.scheduledPage = Math.max(1, Number(page) || 1)
      this.loadScheduled(this.queryParams())
    },
    async onScheduledSendNow(row, done) {
      const finish = typeof done === 'function' ? done : () => {}
      if (!row || row.id == null) {
        finish()
        return
      }
      try {
        await nodeService.patchMessengerAdminNotificationSendNow(row.id)
        if (typeof this.$notify === 'function') {
          this.$notify({
            type: 'success',
            message: 'Envio antecipado (send_date = agora).',
          })
        }
        await this.loadScheduled(this.queryParams())
      } catch (err) {
        if (typeof this.$notify === 'function') {
          this.$notify({
            type: 'danger',
            message: 'Não foi possível disparar agora.',
          })
        }
      } finally {
        finish()
      }
    },
    async loadScheduled(params) {
      this.scheduledLoading = true
      this.scheduledError = ''
      try {
        // status=scheduled → API status==2 (lista Agendadas).
        // page/limit + q (message_data). Com q, API ignora place/produto.
        const search = (this.scheduledSearch || '').trim()
        const query = {
          status: 'scheduled',
          page: this.scheduledPage,
          limit: this.scheduledPerPage,
        }
        if (search) {
          query.q = search
        } else {
          Object.assign(query, params || {})
        }

        const response = await nodeService.getMessengerAdminNotifications(query)
        const data = (response && response.data) || {}
        this.scheduledItems = Array.isArray(data.items) ? data.items : []
        this.scheduledTotal =
          data.total != null ? Number(data.total) : this.scheduledItems.length
        if (data.page != null) this.scheduledPage = Number(data.page) || 1
      } catch (err) {
        this.scheduledItems = []
        this.scheduledTotal = 0
        if (this.isNotFound(err)) {
          this.scheduledError =
            'GET /messenger/admin/notifications indisponível.'
        } else {
          this.scheduledError = 'Não foi possível carregar as agendadas.'
        }
      } finally {
        this.scheduledLoading = false
      }
    },
    async gerar() {
      if (!this.canGenerate) {
        if (typeof this.$notify === 'function') {
          this.$notify({
            type: 'warning',
            message:
              this.contextType === 'gestor'
                ? 'Selecione um gestor (janoo).'
                : 'Selecione um produto (Passeio).',
          })
        }
        return
      }

      this.loading = true
      this.statsError = ''
      this.scheduledPage = 1
      const params = this.queryParams()

      try {
        const statsRes = await nodeService.getMessengerAdminStats(params)
        this.stats = (statsRes && statsRes.data) || null
        this.apiUnavailable = false
        await Promise.all([this.loadModels(), this.loadScheduled(params)])
      } catch (err) {
        this.stats = null
        if (this.isNotFound(err)) {
          this.apiUnavailable = true
          this.statsError = 'API admin indisponível (/messenger/admin/stats).'
        } else {
          this.statsError = 'Não foi possível gerar as estatísticas.'
        }
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.mensageria-admin-body {
  padding-bottom: 2rem;
}

.booking-list-period-label {
  cursor: pointer;
  user-select: none;
  font-size: 0.9rem;
}

.booking-list-period-label span:hover {
  text-decoration: underline;
}

/* Alinhado ao relatório NPS (Pesquisa de satisfação) */
.nps-section-card {
  border: 1px solid #e6eef2;
  box-shadow: none;
}

.nps-section-header {
  background: #e6eef2;
  border-bottom: 1px solid #e6eef2;
  color: #12576e;
  font-weight: 600;
  font-size: 1rem;
}

.nps-section-hint {
  font-size: 0.75rem;
  font-weight: 400;
  color: #6c757d;
}

.nps-product-select {
  width: 100%;
}
</style>

<style>
/* Same period-toggle shell as Operações Dia|Semana|Mês */
.bg-pick {
  background-color: white;
  height: 25px;
  width: fit-content;
  display: flex;
  align-items: center;
  margin-right: 10px;
  margin-left: 10px;
  border-radius: 4px;
}

.bg-pick .btn-letter.active {
  background: #12576e;
  color: #fff;
}

/* Element UI DatePicker wrappers — unscoped so inner input is hidden */
.booking-list-hidden-datepicker {
  position: absolute !important;
  width: 0 !important;
  height: 0 !important;
  min-width: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  opacity: 0 !important;
  overflow: hidden !important;
  pointer-events: none !important;
}

.booking-list-hidden-datepicker .el-input__inner {
  padding: 0 !important;
  height: 0 !important;
  line-height: 0 !important;
  border: none !important;
}
</style>
